<div class="p-8 min-h-screen bg-gray-50">
  <div class="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
    <div>
      <h1 class="text-3xl font-bold text-gray-900">Driver payroll</h1>
      <p class="text-gray-600 mt-1">
        Commission on completed job totals, cash withheld (cash-to-driver), and net pay per pay period.
      </p>
      <p class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 max-w-3xl">
        Payroll figures are operational estimates for your dispatch workflow — not tax, legal, or certified timeclock
        advice. “Job time” is Created→Completed duration per job (approximate).
      </p>
    </div>
    <button
      type="button"
      (click)="exportCsv()"
      [disabled]="exporting || loading"
      class="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium disabled:opacity-60 shrink-0"
    >
      @if (exporting) {
        <i class="fas fa-spinner fa-spin mr-2"></i>
      } @else {
        <i class="fas fa-file-csv mr-2"></i>
      }
      Export CSV
    </button>
  </div>

  <div class="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-100">
    <h2 class="text-sm font-semibold text-gray-800 mb-3">Generate or refresh draft payroll</h2>
    <p class="text-sm text-gray-600 mb-4">
      Creates or updates <strong>Draft</strong> rows from completed jobs in the period (UTC dates). Finalized or paid
      rows are not overwritten.
    </p>
    @if (settingsDriverCommissionPct !== null) {
      <p class="text-sm text-gray-700 mb-4 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2">
        Draft payroll uses the current <strong>driver commission</strong> from Settings:
        <strong>{{ settingsDriverCommissionPct | number: '1.0-2' }}%</strong> of completed job revenue (gross), then
        cash withheld is applied to net pay.
      </p>
    }
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Pay period start</label>
        <input
          type="date"
          [(ngModel)]="genStartDate"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Pay period end</label>
        <input
          type="date"
          [(ngModel)]="genEndDate"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
        />
      </div>
      <div class="md:col-span-2">
        <button
          type="button"
          (click)="generatePayroll()"
          [disabled]="generating"
          class="px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-60"
        >
          @if (generating) {
            <i class="fas fa-spinner fa-spin mr-2"></i>
          } @else {
            <i class="fas fa-calculator mr-2"></i>
          }
          Generate / refresh draft
        </button>
      </div>
    </div>
  </div>

  <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
    <h2 class="text-sm font-semibold text-gray-800 mb-3">Filter list</h2>
    <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Start date</label>
        <input
          type="date"
          [(ngModel)]="filterStartDate"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">End date</label>
        <input
          type="date"
          [(ngModel)]="filterEndDate"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
        <select
          [(ngModel)]="statusFilter"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary bg-white"
        >
          <option value="">All</option>
          <option value="Draft">Draft</option>
          <option value="Finalized">Finalized</option>
          <option value="Paid">Paid</option>
        </select>
      </div>
      <div class="flex items-end gap-2 md:col-span-2">
        <button
          type="button"
          (click)="onFilterApply()"
          [disabled]="loading"
          class="px-4 py-2.5 bg-primary text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-60"
        >
          <i class="fas fa-filter mr-2"></i>Apply
        </button>
        <button
          type="button"
          (click)="clearFilters()"
          [disabled]="loading"
          class="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          Clear
        </button>
      </div>
    </div>
  </div>

  @if (error) {
    <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
      <i class="fas fa-exclamation-circle mr-2"></i>{{ error }}
    </div>
  }

  @if (successMessage) {
    <div class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
      <i class="fas fa-check-circle mr-2"></i>{{ successMessage }}
    </div>
  }

  @if (loading) {
    <div class="bg-white rounded-lg shadow-sm p-12 text-center">
      <i class="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
      <p class="text-gray-600">Loading payroll…</p>
    </div>
  } @else if (rows.length === 0) {
    <div class="bg-white rounded-lg shadow-sm p-12 text-center text-gray-600">
      No payroll rows for this filter. Generate a draft for a pay period above.
    </div>
  } @else {
    <div class="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left font-medium text-gray-700">Driver</th>
              <th class="px-4 py-3 text-left font-medium text-gray-700">Period</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">Jobs</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">Job time (approx.)</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">Revenue</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">%</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">Gross</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">Cash withheld</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">Net pay</th>
              <th class="px-4 py-3 text-left font-medium text-gray-700">Status</th>
              <th class="px-4 py-3 text-right font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            @for (row of rows; track row.id) {
              <tr class="hover:bg-gray-50">
                <td class="px-4 py-3">
                  <div class="font-medium text-gray-900">{{ row.driverName }}</div>
                  @if (row.driverEmail) {
                    <div class="text-xs text-gray-500">{{ row.driverEmail }}</div>
                  }
                </td>
                <td class="px-4 py-3 text-gray-700 whitespace-nowrap">
                  {{ formatPeriod(row.payPeriodStart) }} – {{ formatPeriod(row.payPeriodEnd) }}
                </td>
                <td class="px-4 py-3 text-right">{{ row.totalJobs }}</td>
                <td class="px-4 py-3 text-right text-gray-600">{{ formatJobTimeMinutes(row.totalJobMinutes) }}</td>
                <td class="px-4 py-3 text-right">{{ formatCurrency(row.totalJobRevenue) }}</td>
                <td class="px-4 py-3 text-right">{{ row.commissionPercentage }}%</td>
                <td class="px-4 py-3 text-right">{{ formatCurrency(row.grossEarnings) }}</td>
                <td class="px-4 py-3 text-right">{{ formatCurrency(row.cashCollections) }}</td>
                <td class="px-4 py-3 text-right font-semibold text-gray-900">{{ formatCurrency(row.netPay) }}</td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex px-2 py-0.5 rounded text-xs font-medium"
                    [ngClass]="{
                      'bg-amber-100 text-amber-900': row.status === 'Draft',
                      'bg-blue-100 text-blue-900': row.status === 'Finalized',
                      'bg-green-100 text-green-900': row.status === 'Paid'
                    }"
                  >
                    {{ row.status }}
                  </span>
                </td>
                <td class="px-4 py-3 text-right whitespace-nowrap">
                  @if (row.status === 'Draft') {
                    <button
                      type="button"
                      (click)="finalize(row)"
                      [disabled]="actionLoadingId === row.id"
                      class="text-primary hover:underline text-xs font-medium disabled:opacity-50 mr-2"
                    >
                      Finalize
                    </button>
                  }
                  @if (row.status === 'Finalized') {
                    <button
                      type="button"
                      (click)="markPaid(row)"
                      [disabled]="actionLoadingId === row.id"
                      class="text-green-700 hover:underline text-xs font-medium disabled:opacity-50"
                    >
                      Mark paid
                    </button>
                  }
                  @if (actionLoadingId === row.id) {
                    <i class="fas fa-spinner fa-spin ml-1 text-gray-400"></i>
                  }
                </td>
              </tr>
            }
          </tbody>
          <tfoot class="bg-gray-50 font-medium">
            <tr>
              <td class="px-4 py-3 text-gray-800" colspan="2">Totals (this list)</td>
              <td class="px-4 py-3 text-right text-gray-700">{{ totals().jobs }}</td>
              <td class="px-4 py-3 text-right text-gray-400">—</td>
              <td class="px-4 py-3 text-right">{{ formatCurrency(totals().revenue) }}</td>
              <td class="px-4 py-3"></td>
              <td class="px-4 py-3 text-right">{{ formatCurrency(totals().gross) }}</td>
              <td class="px-4 py-3 text-right">{{ formatCurrency(totals().cash) }}</td>
              <td class="px-4 py-3 text-right text-gray-900">{{ formatCurrency(totals().net) }}</td>
              <td class="px-4 py-3" colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  }
</div>
